import {
  useGetOnlineAccounts,
  useGetStateContext,
  useGetState,
  useGetIntroducerApplication,
} from 'lendingApi/lendingApiHooks';

type TFormattedLinkedBankAccount = {
  bankName: string;
  accountNumberLastFourDigits: string;
};

export const useLinkOpenBankingStatus = () => {
  const { state, stateFetching } = useGetState();
  const { application } = useGetIntroducerApplication();
  const stateKey = application?.customer.state_key;

  const {
    data: stateContextResponse,
    isLoading: stateContextLoading,
    isError: stateContextError,
  } = useGetStateContext(stateKey);

  const {
    data: onlineAccountsResponse,
    isLoading: onlineAccountsLoading,
    isError: onlineAccountsError,
  } = useGetOnlineAccounts(stateKey);

  const applicant = state?.application?.people.find((person) =>
    person.roles.includes('applicant'),
  );

  const linkedBankAccounts: TFormattedLinkedBankAccount[] = [];

  if (stateContextResponse?.data && onlineAccountsResponse?.data) {
    const stateContext = stateContextResponse.data;
    const onlineAccounts = onlineAccountsResponse.data;

    const openBankingProviders = [
      ...(stateContext.open_banking?.banks ?? []),
      ...(stateContext.open_banking.truelayer_banks ?? []),
    ];

    const providerNames = openBankingProviders.map(
      (provider) => provider.bank_name,
    );

    const accountsInOpenBankingProviders = onlineAccounts.filter(
      ({ provider }) => provider && providerNames.includes(provider),
    );

    const activeAccounts = accountsInOpenBankingProviders.filter(
      ({ status }) => status?.code === 'active',
    );

    const enrichedAccounts = activeAccounts.map((account) => {
      const bank = openBankingProviders.find(
        ({ bank_name }) => bank_name === account.provider,
      );
      return { ...account, bank_name: bank?.bank_name || 'Unknown' };
    });

    const linkedBanks = enrichedAccounts.map((account) => ({
      bankName: account.bank_name,
      accounts: account.accounts.map(({ extra = {} }) => ({
        balance: extra.balance,
        accountNumber: extra.account_number,
      })),
    }));

    for (const { bankName, accounts } of linkedBanks) {
      for (const account of accounts) {
        linkedBankAccounts.push({
          bankName,
          accountNumberLastFourDigits: (account.accountNumber as string).slice(
            -4,
          ),
        });
      }
    }
  }

  return {
    linkedBankAccounts,
    isFetchingLinkedBankAccounts:
      stateFetching || stateContextLoading || onlineAccountsLoading,
    isError: stateContextError || onlineAccountsError,
    customerEmail: applicant?.email,
    customerFirstName: applicant?.first_name,
  };
};
